import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import "moment-timezone";

/** Special period values (not ISO week numbers). */
export const PERIOD_YTD = "ytd";
export const PERIOD_MTD = "mtd";

const isSpecialPeriod = (period) =>
  period === PERIOD_YTD || period === PERIOD_MTD;

/** ISO week range: Monday 00:00 → Sunday 23:59:59.999 in the given timezone. */
export const getIsoWeekRange = (weekNum, timezone = "Asia/Riyadh") => {
  const now = moment().tz(timezone);
  const year = now.isoWeekYear();
  const currentWeek = now.isoWeek();
  const parsed = parseInt(weekNum, 10);
  const week = Number.isFinite(parsed)
    ? Math.min(Math.max(parsed, 1), currentWeek)
    : currentWeek;
  // Jan 4 is always in ISO week 1 of its ISO week-year (Monday–Sunday weeks).
  const start = moment
    .tz(`${year}-01-04`, timezone)
    .isoWeek(week)
    .startOf("isoWeek")
    .startOf("day");
  const end = start.clone().endOf("isoWeek").endOf("day");

  return { start, end, week, year, currentWeek };
};

/** Year-to-date: Jan 1 00:00 → today end of day. */
export const getYtdDateRange = (timezone = "Asia/Riyadh") => {
  const now = moment().tz(timezone);
  const start = now.clone().startOf("year").startOf("day");
  const end = now.clone().endOf("day");
  return {
    start,
    end,
    from: start.toISOString(),
    to: end.toISOString(),
    year: now.year(),
  };
};

/** Month-to-date: 1st of current month 00:00 → today end of day. */
export const getMtdDateRange = (timezone = "Asia/Riyadh") => {
  const now = moment().tz(timezone);
  const start = now.clone().startOf("month").startOf("day");
  const end = now.clone().endOf("day");
  return {
    start,
    end,
    from: start.toISOString(),
    to: end.toISOString(),
    year: now.year(),
  };
};

/** Resolve from/to for picker selection (`ytd` | `mtd` | ISO week number). */
export const getPeriodDateRange = (period, timezone = "Asia/Riyadh") => {
  if (period === PERIOD_YTD) {
    const ytd = getYtdDateRange(timezone);
    return { from: ytd.from, to: ytd.to };
  }
  if (period === PERIOD_MTD) {
    const mtd = getMtdDateRange(timezone);
    return { from: mtd.from, to: mtd.to };
  }
  const range = getIsoWeekRange(period, timezone);
  return {
    from: range.start.toISOString(),
    to: range.end.toISOString(),
  };
};

/**
 * Current + previous comparable ranges for `/api/nskpi/mtd_revenue/`.
 * Query params: curMonthStart, curMonthEnd, prevMonthStart, prevMonthEnd
 */
export const getMtdRevenueCompareRanges = (
  period,
  timezone = "Asia/Riyadh",
) => {
  const now = moment().tz(timezone);

  if (period === PERIOD_YTD) {
    const curMonthStart = now.clone().startOf("year").startOf("day");
    const curMonthEnd = now.clone().endOf("day");
    const prevMonthStart = curMonthStart.clone().subtract(1, "year");
    const prevMonthEnd = curMonthEnd.clone().subtract(1, "year");
    return {
      curMonthStart: curMonthStart.toISOString(),
      curMonthEnd: curMonthEnd.toISOString(),
      prevMonthStart: prevMonthStart.toISOString(),
      prevMonthEnd: prevMonthEnd.toISOString(),
    };
  }

  if (period === PERIOD_MTD) {
    const curMonthStart = now.clone().startOf("month").startOf("day");
    const curMonthEnd = now.clone().endOf("day");
    const dayOfMonth = now.date();
    const prevMonth = now.clone().subtract(1, "month");
    const prevMonthStart = prevMonth.clone().startOf("month").startOf("day");
    const prevMonthEnd = prevMonth
      .clone()
      .date(Math.min(dayOfMonth, prevMonth.daysInMonth()))
      .endOf("day");
    return {
      curMonthStart: curMonthStart.toISOString(),
      curMonthEnd: curMonthEnd.toISOString(),
      prevMonthStart: prevMonthStart.toISOString(),
      prevMonthEnd: prevMonthEnd.toISOString(),
    };
  }

  const range = getIsoWeekRange(period, timezone);
  const curMonthStart = range.start.clone();
  const curMonthEnd = range.end.clone();
  const prevMonthStart = curMonthStart.clone().subtract(1, "week");
  const prevMonthEnd = curMonthEnd.clone().subtract(1, "week");
  return {
    curMonthStart: curMonthStart.toISOString(),
    curMonthEnd: curMonthEnd.toISOString(),
    prevMonthStart: prevMonthStart.toISOString(),
    prevMonthEnd: prevMonthEnd.toISOString(),
  };
};

export const formatWeekHint = (start, end) => {
  if (start.year() !== end.year()) {
    return `${start.format("ddd DD MMM YYYY")} - ${end.format("ddd DD MMM YYYY")}`;
  }
  return `${start.format("ddd DD MMM")} - ${end.format("ddd DD MMM YYYY")}`;
};

const getWeekText = (
  weekLabel,
  weekNum,
  currentWeek,
  currentWeekLabel,
  rangeHint = "",
) => {
  const base =
    weekNum === currentWeek
      ? `${weekLabel} ${weekNum} (${currentWeekLabel})`
      : `${weekLabel} ${weekNum}`;
  return rangeHint ? `${base} · ${rangeHint}` : base;
};

const SalesDateRangePicker = ({
  timezone = "Asia/Riyadh",
  weekLabel = "Week",
  currentWeekLabel = "Current Week",
  ytdLabel = "YTD",
  mtdLabel = "MTD",
  selectedWeek,
  onWeekChange,
}) => {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const isYtd = selectedWeek === PERIOD_YTD;
  const isMtd = selectedWeek === PERIOD_MTD;
  const isSpecial = isSpecialPeriod(selectedWeek);

  const weekMeta = useMemo(
    () => getIsoWeekRange(isSpecial ? null : selectedWeek, timezone),
    [isSpecial, selectedWeek, timezone],
  );
  const ytdMeta = useMemo(() => getYtdDateRange(timezone), [timezone]);
  const mtdMeta = useMemo(() => getMtdDateRange(timezone), [timezone]);

  const { currentWeek, year } = weekMeta;
  const start = isYtd ? ytdMeta.start : isMtd ? mtdMeta.start : weekMeta.start;
  const end = isYtd ? ytdMeta.end : isMtd ? mtdMeta.end : weekMeta.end;
  const week = weekMeta.week;

  const weeks = useMemo(
    () =>
      Array.from({ length: currentWeek }, (_, index) => {
        const weekNum = index + 1;
        const range = getIsoWeekRange(weekNum, timezone);
        return {
          weekNum,
          hint: `${range.start.format("DD MMM")} - ${range.end.format("DD MMM")}`,
        };
      }),
    [currentWeek, timezone],
  );

  const ytdHint = `${ytdMeta.start.format("DD MMM")} - ${ytdMeta.end.format("DD MMM")}`;
  const mtdHint = `${mtdMeta.start.format("DD MMM")} - ${mtdMeta.end.format("DD MMM")}`;

  const toggleLabel = isYtd
    ? ytdLabel
    : isMtd
      ? mtdLabel
      : getWeekText(weekLabel, week, currentWeek, currentWeekLabel);

  const headerLabel = isYtd
    ? `${ytdLabel} (${formatWeekHint(start, end)})`
    : isMtd
      ? `${mtdLabel} (${formatWeekHint(start, end)})`
      : `${weekLabel} ${year} (${formatWeekHint(start, end)})`;

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);

    const list = listRef.current;
    const selected = list?.querySelector(".is-selected");
    if (list && selected) {
      list.scrollTop = Math.max(
        0,
        selected.offsetTop - list.clientHeight + selected.offsetHeight,
      );
    }

    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, week, isYtd, isMtd]);

  return (
    <div className="skd-week-range" ref={rootRef}>
      <label className="skd-week-range-label">{headerLabel}</label>
      <div className="skd-week-range-row">
        <div className={`skd-week-range-select-wrap${open ? " is-open" : ""}`}>
          <button
            type="button"
            className="skd-week-range-toggle"
            onClick={() => setOpen((prev) => !prev)}
          >
            <span className="skd-week-range-toggle-label">{toggleLabel}</span>
            <svg
              className="skd-week-range-chevron-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </button>
          {open && (
            <ul ref={listRef} className="skd-week-range-menu" role="listbox">
              <li
                className={`skd-week-range-option${
                  isYtd ? " is-selected" : ""
                }`}
                role="option"
                aria-selected={isYtd}
                onClick={() => {
                  onWeekChange(PERIOD_YTD);
                  setOpen(false);
                }}
              >
                {ytdLabel} · {ytdHint}
              </li>
              <li
                className={`skd-week-range-option${
                  isMtd ? " is-selected" : ""
                }`}
                role="option"
                aria-selected={isMtd}
                onClick={() => {
                  onWeekChange(PERIOD_MTD);
                  setOpen(false);
                }}
              >
                {mtdLabel} · {mtdHint}
              </li>
              {weeks.map(({ weekNum, hint }) => (
                <li
                  key={weekNum}
                  className={`skd-week-range-option${
                    !isSpecial && weekNum === week ? " is-selected" : ""
                  }`}
                  role="option"
                  aria-selected={!isSpecial && weekNum === week}
                  onClick={() => {
                    onWeekChange(weekNum);
                    setOpen(false);
                  }}
                >
                  {getWeekText(
                    weekLabel,
                    weekNum,
                    currentWeek,
                    currentWeekLabel,
                    hint,
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesDateRangePicker;
